import { omit } from "lodash";
import reduceReducers from "reduce-reducers";

import {
  ADD_BUSINESS_PROPERTY_COMMIT,
  ADD_BUSINESS_PROPERTY_OFFLINE,
  ADD_CONSIGNMENT_OFFLINE,
  ADD_SALE_LOT_COMMIT,
  ADD_SALE_LOT_OFFLINE,
  ADD_TEMP_BUYER_WAY,
  ATTACHMENT,
  BUSINESS,
  MERGE_BUSINESSES,
  PATCH_CONSIGNMENT_OFFLINE,
  SALE_LOT,
  TEMPORARY_BUSINESS_RESOLVE_STARTED,
} from "constants/actionTypes";

import {
  apiModelOfflineCommentReducer,
  apiModelOfflineCreateReducer,
  apiModelOfflineDeleteReducer,
  apiModelOfflineFetchReducer,
  apiModelOfflineUpdateReducer,
  bulkUpdateCases,
  reduceById,
} from "lib/reducers";
import { deserializeBusiness } from "lib/serializers/businesses";

const create = apiModelOfflineCreateReducer(BUSINESS, {
  deserializer: deserializeBusiness,
});

const deleteReducer = apiModelOfflineDeleteReducer(BUSINESS);

const fetch = apiModelOfflineFetchReducer(BUSINESS, {
  deserializer: deserializeBusiness,
  clearOnRequest: true,
});

const update = apiModelOfflineUpdateReducer(BUSINESS, {
  deserializer: deserializeBusiness,
});

const comment = apiModelOfflineCommentReducer(BUSINESS);

const createBuyerWayFromSaleLotChange = (state, saleLot) => {
  const businessId = saleLot.buyer_id;
  const buyerWay = saleLot.buyer_way;
  if (businessId && buyerWay) {
    // Find any existing entries.
    const existingEntryIndex = state.byId[businessId].buyerWays.findIndex(
      existingBuyerWay =>
        existingBuyerWay.name.toLowerCase() === buyerWay.name.toLowerCase(),
    );

    const newBuyerWays = [...state.byId[businessId].buyerWays];

    // It was found, and is showing - nothing to do here.
    if (existingEntryIndex >= 0) {
      // It was found.  If it's already showing, nothing to do.
      if (state.byId[businessId].buyerWays[existingEntryIndex].isShown) {
        return state;
      }
      // Otherwise flip it to shown .
      newBuyerWays[existingEntryIndex].isShown = true;
    } else {
      // add a new one.
      newBuyerWays.push({
        ...buyerWay,
        isShown: true,
        saleyardIds: [],
        destinationPropertyId: null,
      });
    }

    return {
      ...state,
      byId: {
        ...state.byId,
        [businessId]: {
          ...state.byId[businessId],
          buyerWays: newBuyerWays,
        },
      },
    };
  } else {
    return state;
  }
};
const updateBuyerWayFromSaleLotChange = (state, saleLot) => {
  const businessId = saleLot.buyer_id;
  const buyerWay = saleLot.buyer_way;
  if (businessId && buyerWay) {
    // The buyer way may/will be in there with a temp id - go update it.
    const newBuyerWays = state.byId[businessId].buyerWays.map(
      existingBuyerWay => {
        if (
          existingBuyerWay.name.toLowerCase() === buyerWay.name.toLowerCase()
        ) {
          // Overwrite the id if set, otherwise keep all info the same.
          return {
            ...existingBuyerWay,
            ...buyerWay,
          };
        } else {
          return existingBuyerWay;
        }
      },
    );

    return {
      ...state,
      byId: {
        ...state.byId,
        [businessId]: {
          ...state.byId[businessId],
          buyerWays: newBuyerWays,
        },
      },
    };
  } else {
    return state;
  }
};

function buyerWayEventReducer(state, action) {
  switch (action.type) {
    // A buyer way added from the auction screen gets submitted with the sale lot.
    case ADD_TEMP_BUYER_WAY: {
      const { buyerId, buyerWay } = action;
      return {
        ...state,
        byId: {
          ...state.byId,
          [buyerId]: {
            ...state.byId[buyerId],
            buyerWays: [
              ...state.byId[buyerId].buyerWays,
              {
                ...buyerWay,
                isShown: true,
                saleyardIds: [],
                destinationPropertyId: null,
              },
            ],
          },
        },
      };
    }

    // A buyer way can be added via submitting a sale lot - add it to the business straight away (we'll likely
    // get a subsequent pusher message)
    case ADD_SALE_LOT_OFFLINE: {
      const saleLot = action.payload || {};
      return createBuyerWayFromSaleLotChange(state, saleLot);
    }
    case SALE_LOT.UPDATE.REQUEST: {
      const updatedSaleLot = action.payload.patch || {};
      const existingSaleLot = action.payload.saleLot || {};
      const relevantSaleLot = {
        // When adding a new Buyer Way to a Sale Lot, we need to know which Business to put the new Buyer Way onto.
        // When the Buyer Way is update, and the Buyer has not changed (as indicated by buyer_way being set and buyer_id being undefined),
        // the system refers to the Buyer that is already on the Sale Lot.
        buyer_id:
          updatedSaleLot.buyer_id === undefined
            ? existingSaleLot.buyer_id
            : updatedSaleLot.buyer_id,
        buyer_way:
          updatedSaleLot.buyer_way === undefined
            ? existingSaleLot.buyer_way
            : updatedSaleLot.buyer_way,
      };
      return createBuyerWayFromSaleLotChange(state, relevantSaleLot);
    }

    case SALE_LOT.UPDATE_BULK.REQUEST: {
      return action.payload.reduce((nextState, saleLot) => {
        nextState = createBuyerWayFromSaleLotChange(nextState, saleLot);
        return nextState;
      }, state);
    }

    case SALE_LOT.UPDATE_BULK.SUCCESS: {
      return action.payload.reduce((nextState, saleLot) => {
        nextState = updateBuyerWayFromSaleLotChange(nextState, saleLot);
        return nextState;
      }, state);
    }

    case ADD_SALE_LOT_COMMIT:
    case SALE_LOT.UPDATE.SUCCESS: {
      return updateBuyerWayFromSaleLotChange(state, action.payload);
    }
    default:
      return state;
  }
}

function consignmentEventReducer(state, action) {
  switch (action.type) {
    // When adding or updating a consignment with a branch, that may get set on this business.
    case ADD_CONSIGNMENT_OFFLINE:
    case PATCH_CONSIGNMENT_OFFLINE: {
      const { branch_id, vendor_id } = action.payload;
      if (
        branch_id &&
        state.byId[vendor_id] &&
        state.byId[vendor_id].branchId !== branch_id
      ) {
        return {
          ...state,
          byId: {
            ...state.byId,
            [vendor_id]: {
              ...state.byId[vendor_id],
              branchId: branch_id,
            },
          },
        };
      } else {
        return state;
      }
    }
    default:
      return state;
  }
}

function attachmentEventReducer(state, action) {
  switch (action.type) {
    // When adding or updating an attachment with a branch, that may get set on this business.
    case ATTACHMENT.UPDATE.REQUEST: {
      const { vendor } = action;
      const { branch_id } = action.attachment;

      if (
        vendor &&
        branch_id &&
        state.byId[vendor.id] &&
        state.byId[vendor.id].branch_id !== branch_id
      ) {
        return {
          ...state,
          byId: {
            ...state.byId,
            [vendor.id]: {
              ...state.byId[vendor.id],
              branch_id,
            },
          },
        };
      } else {
        return state;
      }
    }
    default:
      return state;
  }
}

function propertyEventReducer(state, action) {
  switch (action.type) {
    // A buyer way added from the auction screen gets submitted with the sale lot.
    case ADD_BUSINESS_PROPERTY_OFFLINE: {
      const { meta, payload } = action;
      const { businessId, tempId } = meta;
      const { name } = payload;
      return {
        ...state,
        byId: {
          ...state.byId,
          [businessId]: {
            ...state.byId[businessId],
            properties: [
              ...state.byId[businessId].properties,
              { id: tempId, name, isShown: true },
            ],
          },
        },
      };
    }
    case ADD_BUSINESS_PROPERTY_COMMIT: {
      // More or less the same as add property, but deducping.
      const { meta, payload } = action;
      const { businessId, tempId } = meta;
      // Remove the temp one we had
      const updatedProperties = state.byId[businessId].properties.filter(
        property => property.id !== tempId,
      );
      // And add the committed version.
      updatedProperties.push({
        isShown: true,
        id: payload.id,
        PIC: payload.PIC,
      });

      return {
        ...state,
        byId: {
          ...state.byId,
          [businessId]: {
            ...state.byId[businessId],
            properties: updatedProperties,
          },
        },
      };
    }
    default:
      return state;
  }
}

function bulkReducer(state, action) {
  switch (action.type) {
    case BUSINESS.UPDATE_BULK.REQUEST:
    case BUSINESS.UPDATE_BULK.SUCCESS:
    case BUSINESS.UPDATE_BULK.FROM_SOCKET: {
      return bulkUpdateCases(state, action, "byId", deserializeBusiness);
    }
    default:
      return state;
  }
}

// Verify similar to a fetch - just a GET for most meanings of the word from the client side.
function createFromGlobalReducer(state, action) {
  switch (action.type) {
    case BUSINESS.GET_OR_CREATE_GLOBAL_BUSINESS.SUCCESS: {
      return {
        ...state,
        isFetching: false,
        byId: {
          ...state.byId,
          ...reduceById([deserializeBusiness(action.payload)]),
        },
      };
    }
    default:
      return state;
  }
}

/*
 Make simple multi-line string from merge businesses error response e.g.

 {
    "business_default_destination": [
        "['Source business has business default destination']"
    ],
    "recipient_documents": [
        "['Source business has recipient documents']"
    ],
    "report_jobs": [
        "['Source business has report jobs']"
    ]
 }
*/
function mergeBusinessesErrorResponseToString(response) {
  if (typeof response !== "object") {
    return "Unexpected error merging businesses";
  }
  return Object.values(response)
    .reduce(
      (prev, curr) => [...prev, ...curr.map(e => e.replace(/\[|'|\]/g, ""))],
      [],
    )
    .join("\n");
}

function mergeBusinessesReducer(state, action) {
  switch (action.type) {
    case MERGE_BUSINESSES.CREATE.SUCCESS: {
      // The API deletes the source Business on merge - drop it now rather
      // than waiting for the next changes fetch.
      const sourceBusinessId = action.options?.sourceBusinessId;
      if (!sourceBusinessId || !state.byId[sourceBusinessId]) {
        return state;
      }
      const byId = { ...state.byId };
      delete byId[sourceBusinessId];
      return { ...state, byId };
    }
    case MERGE_BUSINESSES.CREATE.FAILURE: {
      const errorResponse = action?.payload?.response;
      return {
        ...state,
        error: mergeBusinessesErrorResponseToString(errorResponse),
      };
    }
    default:
      return state;
  }
}

const settledResolveBusinessId = action => {
  switch (action.type) {
    case BUSINESS.UPDATE.SUCCESS:
      return action.payload?.id;
    case BUSINESS.UPDATE.FAILURE:
      return action.id;
    case MERGE_BUSINESSES.CREATE.SUCCESS:
    case MERGE_BUSINESSES.CREATE.FAILURE:
      return action.options?.sourceBusinessId;
    case BUSINESS.GET_OR_CREATE_GLOBAL_BUSINESS.FAILURE:
      return action.mergeSourceBusinessId;
    default:
      return undefined;
  }
};

// Temporary Business ids with a resolve request still in flight.
function temporaryBusinessResolvesReducer(state, action) {
  const resolves = state.temporaryBusinessResolves || {};
  if (action.type === TEMPORARY_BUSINESS_RESOLVE_STARTED) {
    return {
      ...state,
      temporaryBusinessResolves: { ...resolves, [action.businessId]: true },
    };
  }
  const businessId = settledResolveBusinessId(action);
  if (!businessId || !resolves[businessId]) {
    return state;
  }
  return {
    ...state,
    temporaryBusinessResolves: omit(resolves, businessId),
  };
}

const businessV2Reducer = reduceReducers(
  create,
  comment,
  deleteReducer,
  fetch,
  update,
  buyerWayEventReducer,
  consignmentEventReducer,
  attachmentEventReducer,
  propertyEventReducer,
  bulkReducer,
  createFromGlobalReducer,
  mergeBusinessesReducer,
  temporaryBusinessResolvesReducer,
);

export default businessV2Reducer;
